import { format, formatDistanceToNowStrict, isValid, parseISO, type Locale } from 'date-fns';
import { enUS, he, ro, ru } from 'date-fns/locale';

/**
 * Formatting for display.
 *
 * The clinic reads and writes `DD.MM.YYYY`; the API speaks ISO. Everything
 * that crosses that line goes through here, so the conversion exists in one
 * place rather than being re-derived at each call site — which is where the
 * original's off-by-one date bugs came from.
 *
 * Numbers and the names of days and months follow the active language, set by
 * `setFormatLocale` from the i18n `languageChanged` handler. Romanian is the
 * default and its output is unchanged: `ro` maps to `ro-MD` for `Intl` and to
 * the hand-written capitalised month names below, because those are what the
 * clinic's paperwork uses.
 */

const DISPLAY_DATE = 'dd.MM.yyyy';
const DISPLAY_DATE_TIME = 'dd.MM.yyyy HH:mm';

/** BCP-47 tag for `Intl`, per app language. `ro` stays `ro-MD` deliberately. */
const INTL_LOCALE: Record<string, string> = {
  ro: 'ro-MD',
  ru: 'ru-RU',
  en: 'en-US',
  he: 'he-IL',
};

/** `date-fns` locale for relative times, per app language. */
const DATEFNS_LOCALE: Record<string, Locale> = { ro, ru, en: enUS, he };

let activeLang = 'ro';

/**
 * Point the formatters at a language. Called from `@/i18n` on every language
 * change; the base subtag is enough (`he-IL` formats the same as `he`).
 */
export function setFormatLocale(lang: string): void {
  activeLang = (lang || 'ro').split('-')[0];
}

function intlLocale(): string {
  return INTL_LOCALE[activeLang] ?? 'ro-MD';
}

function dateFnsLocale(): Locale {
  return DATEFNS_LOCALE[activeLang] ?? ro;
}

export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(value) : value;
  return isValid(d) ? d : null;
}

/** ISO date or timestamp -> `31.08.2026`. */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  return d ? format(d, DISPLAY_DATE) : fallback;
}

export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  return d ? format(d, DISPLAY_DATE_TIME) : fallback;
}

export function formatTime(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  return d ? format(d, 'HH:mm') : fallback;
}

/** `acum 3 zile`. */
export function formatRelative(value: string | Date | null | undefined, fallback = '—'): string {
  const d = toDate(value);
  return d ? formatDistanceToNowStrict(d, { addSuffix: true, locale: dateFnsLocale() }) : fallback;
}

/**
 * Day and month names, capitalised.
 *
 * Not `date-fns`' `ro` locale, which lower-cases both — the reference system
 * writes `Vineri, 04 Septembrie 2026` on every calendar header, and the
 * clinic reads that header beside a printed day sheet that says the same.
 */
export const DAYS_RO = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
export const MONTHS_RO = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
];

// Day names are indexed by `Date.getDay()`, so every table starts at Sunday.
// These are provisional translations pending professional review; Romanian is
// authoritative and unchanged.
const DAYS_BY_LANG: Record<string, string[]> = {
  ro: DAYS_RO,
  ru: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  he: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
};

const MONTHS_BY_LANG: Record<string, string[]> = {
  ro: MONTHS_RO,
  ru: [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  he: [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
  ],
};

function dayNames(): string[] {
  return DAYS_BY_LANG[activeLang] ?? DAYS_RO;
}

function monthNames(): string[] {
  return MONTHS_BY_LANG[activeLang] ?? MONTHS_RO;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `Vineri, 04 Septembrie 2026` — the calendar's day title. */
export function formatDayLabel(value: string | Date): string {
  const d = toDate(value);
  if (!d) return '';
  return `${dayNames()[d.getDay()]}, ${pad2(d.getDate())} ${monthNames()[d.getMonth()]} ${d.getFullYear()}`;
}

/** `Vineri, 04 Septembrie` — a day heading inside a range that names the year once. */
export function formatDayShort(value: string | Date): string {
  const d = toDate(value);
  if (!d) return '';
  return `${dayNames()[d.getDay()]}, ${pad2(d.getDate())} ${monthNames()[d.getMonth()]}`;
}

/** `04 Septembrie` — one end of a week range. */
export function formatDayMonth(value: string | Date): string {
  const d = toDate(value);
  return d ? `${pad2(d.getDate())} ${monthNames()[d.getMonth()]}` : '';
}

/** `Septembrie 2026`. */
export function formatMonthLabel(value: string | Date): string {
  const d = toDate(value);
  return d ? `${monthNames()[d.getMonth()]} ${d.getFullYear()}` : '';
}

/**
 * Ziua clinicii pentru o valoare venită din API.
 *
 * `finished_at`, `paid_at`, `created_at` sunt `timestamptz`: ajung aici ca
 * `2026-09-06T21:12:09.874Z`. Tăierea primelor zece caractere dă **ziua UTC**,
 * iar la Chișinău (UTC+3) asta e ziua de ieri pentru orice s-a întâmplat între
 * miezul nopții și ora trei. O procedură terminată la 00:30 se tipărea pe fișa
 * 043/e cu data zilei precedente, iar jurnalul de activitate o grupa sub ziua
 * greșită.
 *
 * Un șir care e deja o zi curată (`2026-09-07`) se întoarce neatins: e ziua
 * clinicii, nu un moment, iar parsarea lui ar reintroduce exact eroarea în
 * fusurile de la vest de Greenwich.
 */
export function localDay(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return value.trim();
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? '' : toApiDate(d);
}

/** A Date -> the `YYYY-MM-DD` the API expects, in local time. */
export function toApiDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  // Deliberately not toISOString(): that converts to UTC, and an appointment
  // at 00:30 in Chișinău would be filed on the previous day.
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Money, written the way the reference system writes it: the number grouped
 * for `ro-MD`, a space, then the currency **code**.
 *
 * Not `Intl` with `style: 'currency'`. That substitutes the locale's own
 * symbol — for MDL in ro-MD it renders `14.000 L`, where the original, and
 * every printed receipt and report that came out of it, says `14.000 MDL`.
 * The clinic reads these figures next to paperwork; the unit has to be the
 * one on the paperwork.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency = 'MDL',
  options: { decimals?: boolean } = {},
): string {
  const value = Number(amount ?? 0);
  const decimals = options.decimals ?? !Number.isInteger(value);
  const n = new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
  return `${n} ${currency}`;
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  return new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(value ?? 0));
}

/**
 * A stock quantity: rounded to the 3 decimals the numeric columns keep, with a
 * thousands separator and no trailing zeros — so `12.3330000` reads `12,333`
 * and `1000` reads `1 000`, not a raw float in a table cell.
 */
export function formatQty(value: number | null | undefined): string {
  const v = Math.round(Number(value ?? 0) * 1000) / 1000;
  return new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 3 }).format(v);
}

/**
 * A percentage the way the clinic writes it: `47,8%`, `20%`.
 *
 * `${value}%` printed the JavaScript number, `47.8%`, and a Romanian reader
 * takes that point for a thousands separator. Up to `maxDecimals` places and
 * none when the value is whole; a dash when there is no value, rather than
 * `NaN%`.
 */
export function formatPercent(value: number | null | undefined, maxDecimals = 1): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(Number(value));
  return `${n}%`;
}

/** `hour`/`min` on an appointment -> `09:30`. */
export function formatSlot(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/** Two letters for an avatar, from a full name. */
export function initialsOf(name: string | null | undefined): string {
  const words = String(name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  // The honorific is not part of the name, and taking its letter makes every
  // doctor in the clinic a "D" — four avatars reading DE, DA, DM, DC, which
  // is four avatars telling you nothing. Dropped unless it is all there is.
  const named = words.filter((w) => !/^(dr|dr\.|doctor|d-?na|dl)$/i.test(w));
  return (named.length ? named : words)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * Every way of writing a Moldovan number, shown one way: `+373 69 123 456`.
 *
 * The same reading the server stores numbers by (`normalizePhone` in the
 * API): `069 123 456`, `69123456`, `37369123456` and `+373 69 123 456` are
 * one number, and a log listing the same patient three ways — with a plus,
 * without, with a trunk zero — looks like three patients. Anything else is
 * shown exactly as it is: better an unformatted foreign number than a mangled
 * one, and a note beside a number is part of what somebody wrote.
 */
export function formatPhone(phone: string | null | undefined): string {
  const raw = String(phone ?? '').trim();
  if (!raw) return '';
  if (!/^[+0-9 ().-]+$/.test(raw)) return raw;

  const digits = raw.replace(/\D/g, '');
  const national = raw.startsWith('+')
    ? digits.startsWith('373')
      ? digits.slice(3)
      : null
    : digits.startsWith('00373')
      ? digits.slice(5)
      : digits.startsWith('373') && digits.length === 11
        ? digits.slice(3)
        : digits.startsWith('0') && digits.length === 9
          ? digits.slice(1)
          : digits.length === 8
            ? digits
            : null;

  if (national === null || national.length !== 8) return raw;
  return `+373 ${national.slice(0, 2)} ${national.slice(2, 5)} ${national.slice(5)}`;
}

/**
 * Numărul cu substantivul lui, acordat românește.
 *
 * Româna are **trei** forme, nu două, iar aplicația le folosea pe două — deci
 * scria „1 zile", „1 materiale", „1 poziții", și niciodată „21 de zile".
 *
 * Regula: 1 cere singularul; 2–19 cer pluralul simplu; de la 20 în sus pluralul
 * vine cu „de". Iar ce decide nu e numărul întreg, ci **ultimele două cifre**:
 * 101 zile, dar 121 de zile; 1.000 de zile, dar 1.001 zile. De asta se ia
 * restul la 100 înainte de comparație.
 *
 * Zero merge cu „de", ca în „0 de zile"? Nu — româna spune „0 zile", deci zero
 * intră pe ramura pluralului simplu.
 *
 * @example plural(1, 'zi', 'zile')   // „1 zi"
 * @example plural(5, 'zi', 'zile')   // „5 zile"
 * @example plural(21, 'zi', 'zile')  // „21 de zile"
 */
export function plural(n: number, one: string, many: string): string {
  const abs = Math.abs(Math.trunc(n));
  if (abs === 1) return `${n} ${one}`;
  const lastTwo = abs % 100;
  const needsDe = lastTwo === 0 ? abs !== 0 : lastTwo >= 20;
  return needsDe ? `${n} de ${many}` : `${n} ${many}`;
}

/**
 * Un text care se citește ca zero — indiferent cum e formatat.
 *
 * „0", „0 MDL", „0,00 MDL", „0%" sunt toate zero; „10" nu e. Testul e absența
 * oricărei cifre de la 1 la 9, fiindcă tot ce se poate afla în jurul cifrelor
 * — simbol de monedă, separator de mii, procent — e punctuație.
 *
 * Există fiindcă varianta naivă, `String(value) === '0'`, rata exact valorile
 * care contează: pe Dashboard, casa goală de dimineață trecea prin
 * `formatMoney` și ajungea „0 MDL", deci se desena la fel de aprins ca cele 31
 * de programări ale zilei.
 */
export function isZeroText(v: string | number): boolean {
  return !/[1-9]/.test(String(v));
}

/**
 * Doar cuvântul din `plural()`, fără cifră.
 *
 * Când numărul e scris separat — într-un `<strong>`, sau într-o propoziție de
 * forma „3 din 21 de …" — `plural()` l-ar dubla. Regula lui „de" rămâne a lui;
 * se taie doar numărul din față.
 */
export function pluralWord(n: number, one: string, many: string): string {
  return plural(n, one, many).replace(/^-?\d+\s/, '');
}
